import type {
  ApiResponse,
  GetCampaignsResponse,
  GetCampaignResponse,
  GetCampaignMetricsResponse,
  GetCampaignSummaryResponse,
  RefreshCacheResponse,
  GetMetricConfigResponse,
  UpdateMetricConfigRequest,
  UpdateMetricConfigResponse,
  GetGlobalRevenueResponse,
  GetGeoRevenueResponse,
  GetConsolidatedRevenueResponse,
  GetRevenueGoalsResponse,
  UpdateRevenueGoalsRequest,
  UpdateRevenueGoalsResponse,
  ExecutionBusinessLine,
  GetDashboardConfigResponse,
  UpdateDashboardConfigRequest,
  UpdateDashboardConfigResponse,
  GetChecklistResponse,
  UpdateChecklistItemRequest,
  UpdateChecklistItemResponse,
  GetStatusSummaryResponse,
  GetBatchSummariesResponse,
  GetReportResponse,
  GetExecutionReportLinkResponse,
  SetExecutionReportLinkRequest,
  SetExecutionReportLinkResponse,
  DeleteExecutionReportLinkResponse,
  UpdateExecutionStatusRequest,
  UpdateExecutionStatusResponse,
  CampaignMetadata,
  MetricDefinition,
  WidgetConfig,
  LayoutItem,
  MetricFormat,
  MetricGoal,
  ListOptimizationReportsResponse,
  GetOptimizationReportResponse,
  DecideSuggestionRequest,
  DecideSuggestionResponse,
  ListAppliedOptimizationsResponse,
} from '@monitoring-panel/types';

/** A failed API call, keeping the HTTP status so callers can tell a 404 or a 409 apart. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/** What a trader can do with a suggestion; each is its own POST route. */
export type SuggestionDecision = 'approve' | 'reject' | 'mark-applied';

/** A single AI-suggested calculated metric (mirrors the backend ai.service shape). */
export interface AIMetricSuggestion {
  name: string;
  formula: string;
  format: MetricFormat;
  decimals: number;
  rationale: string;
  goal?: MetricGoal & { rationale?: string };
}

interface SuggestWidgetsBody {
  metadata: CampaignMetadata;
  availableColumns: string[];
  availableMetrics: MetricDefinition[];
  campaignData: Array<Record<string, unknown>>;
}

interface SuggestMetricsBody {
  metadata: CampaignMetadata;
  availableColumns: string[];
  existingMetrics: MetricDefinition[];
  campaignData: Array<Record<string, unknown>>;
}

const API_BASE = '/api';
const AUTH_SERVICE_URL = import.meta.env.VITE_AUTH_SERVICE_URL || 'https://auth.advia.tech';

function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem('authSession');
    if (stored) {
      const session = JSON.parse(stored);
      return session?.token ?? null;
    }
  } catch {
    // Invalid JSON in storage
  }
  return null;
}

function redirectToLogin(): never {
  const currentUrl = window.location.href;
  const loginUrl = new URL(`${AUTH_SERVICE_URL}/`);
  loginUrl.searchParams.set('redirect_to', currentUrl);
  window.location.href = loginUrl.toString();
  throw new Error('Redirecting to login');
}

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options?.headers as Record<string, string>,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    localStorage.removeItem('authSession');
    redirectToLogin();
  }

  const data = (await response.json()) as ApiResponse<T>;

  if (!data.success) {
    throw new ApiError(
      data.error?.message || 'API request failed',
      response.status,
      data.error?.code ?? null
    );
  }

  return data.data as T;
}

export const api = {
  campaigns: {
    list: (params?: { status?: number[]; search?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) {
        searchParams.set('status', params.status.join(','));
      }
      if (params?.search) {
        searchParams.set('search', params.search);
      }
      const query = searchParams.toString();
      return fetchApi<GetCampaignsResponse>(`/campaigns${query ? `?${query}` : ''}`);
    },

    get: (executionId: string) =>
      fetchApi<GetCampaignResponse>(`/campaigns/${executionId}`),

    getMetrics: (executionId: string) =>
      fetchApi<GetCampaignMetricsResponse>(`/campaigns/${executionId}/metrics`),

    getSummary: (executionId: string) =>
      fetchApi<GetCampaignSummaryResponse>(`/campaigns/${executionId}/summary`),
  },

  cache: {
    refreshAll: () =>
      fetchApi<RefreshCacheResponse>('/cache/refresh', { method: 'POST' }),

    refresh: (executionId: string) =>
      fetchApi<RefreshCacheResponse>(`/cache/refresh/${executionId}`, { method: 'POST' }),

    refreshMultiple: (executionIds: string[]) =>
      fetchApi<RefreshCacheResponse>('/cache/refresh', {
        method: 'POST',
        body: JSON.stringify({ executionIds }),
      }),
  },

  config: {
    getMetrics: (executionId: string) =>
      fetchApi<GetMetricConfigResponse>(`/config/metrics/${executionId}`),

    updateMetrics: (executionId: string, data: UpdateMetricConfigRequest) =>
      fetchApi<UpdateMetricConfigResponse>(`/config/metrics/${executionId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    validateFormula: (params: {
      formula: string;
      availableColumns?: string[];
      sampleData?: Record<string, number>;
    }) =>
      fetchApi<{
        valid: boolean;
        dependencies: string[];
        missingColumns?: string[];
        error?: string;
        preview?: number;
      }>('/config/metrics/validate', {
        method: 'POST',
        body: JSON.stringify(params),
      }),

  },

  ai: {
    suggestWidgets: (body: SuggestWidgetsBody) =>
      fetchApi<{ widgets: WidgetConfig[]; layout: LayoutItem[] }>('/ai/widgets/suggest', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    suggestMetrics: (body: SuggestMetricsBody) =>
      fetchApi<{
        accepted: AIMetricSuggestion[];
        rejected: Array<{ suggestion: AIMetricSuggestion; reason: string }>;
      }>('/ai/metrics/suggest', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  revenue: {
    getGlobal: (startDate: string, endDate: string, scope: ExecutionBusinessLine) =>
      fetchApi<GetGlobalRevenueResponse>(
        `/revenue/global?startDate=${startDate}&endDate=${endDate}&scope=${scope}`
      ),

    getGeo: (startDate: string, endDate: string) =>
      fetchApi<GetGeoRevenueResponse>(
        `/revenue/geo?startDate=${startDate}&endDate=${endDate}`
      ),

    getConsolidated: (startDate: string, endDate: string) =>
      fetchApi<GetConsolidatedRevenueResponse>(
        `/revenue/consolidated?startDate=${startDate}&endDate=${endDate}`
      ),

    getGoals: () => fetchApi<GetRevenueGoalsResponse>('/revenue/goals'),

    updateGoals: (data: UpdateRevenueGoalsRequest) =>
      fetchApi<UpdateRevenueGoalsResponse>('/revenue/goals', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  dashboards: {
    get: (executionId: string) =>
      fetchApi<GetDashboardConfigResponse>(`/dashboards/${executionId}`),

    save: (executionId: string, data: UpdateDashboardConfigRequest) =>
      fetchApi<UpdateDashboardConfigResponse>(`/dashboards/${executionId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (executionId: string) =>
      fetchApi<{ deleted: boolean }>(`/dashboards/${executionId}`, {
        method: 'DELETE',
      }),
  },

  executionNotes: {
    getChecklist: (campaignName: string) =>
      fetchApi<GetChecklistResponse>(`/execution-notes/${encodeURIComponent(campaignName)}/checklist`),

    updateChecklistItem: (campaignName: string, data: UpdateChecklistItemRequest) =>
      fetchApi<UpdateChecklistItemResponse>(`/execution-notes/${encodeURIComponent(campaignName)}/checklist/item`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    getSummary: (campaignName: string) =>
      fetchApi<GetStatusSummaryResponse>(`/execution-notes/${encodeURIComponent(campaignName)}/summary`),

    getBatchSummaries: (campaignNames: string[]) =>
      fetchApi<GetBatchSummariesResponse>(`/execution-notes/summaries?campaignNames=${campaignNames.map(encodeURIComponent).join(',')}`),
  },

  executions: {
    updateStatus: (executionId: string, data: UpdateExecutionStatusRequest) =>
      fetchApi<UpdateExecutionStatusResponse>(
        `/executions/${encodeURIComponent(executionId)}/status`,
        {
          method: 'PATCH',
          body: JSON.stringify(data),
        }
      ),
  },

  reports: {
    get: (reportId: string) =>
      fetchApi<GetReportResponse>(`/reports/${encodeURIComponent(reportId)}`),

    getLink: (executionId: string) =>
      fetchApi<GetExecutionReportLinkResponse>(
        `/executions/${encodeURIComponent(executionId)}/report`
      ),

    setLink: (executionId: string, data: SetExecutionReportLinkRequest) =>
      fetchApi<SetExecutionReportLinkResponse>(
        `/executions/${encodeURIComponent(executionId)}/report`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      ),

    deleteLink: (executionId: string) =>
      fetchApi<DeleteExecutionReportLinkResponse>(
        `/executions/${encodeURIComponent(executionId)}/report`,
        { method: 'DELETE' }
      ),
  },

  optimizations: {
    listReports: (executionId: string) =>
      fetchApi<ListOptimizationReportsResponse>(
        `/executions/${encodeURIComponent(executionId)}/optimization-reports`
      ),

    /** `reportDate` is YYYY-MM-DD or `latest`. */
    getReport: (executionId: string, reportDate: string) =>
      fetchApi<GetOptimizationReportResponse>(
        `/executions/${encodeURIComponent(executionId)}/optimization-reports/${encodeURIComponent(reportDate)}`
      ),

    decide: (
      executionId: string,
      reportDate: string,
      suggestionKey: string,
      decision: SuggestionDecision,
      body: DecideSuggestionRequest = {}
    ) =>
      fetchApi<DecideSuggestionResponse>(
        `/executions/${encodeURIComponent(executionId)}/optimization-reports/${encodeURIComponent(reportDate)}` +
          `/suggestions/${encodeURIComponent(suggestionKey)}/${decision}`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        }
      ),

    listApplied: (executionId: string, lineItemId?: string) => {
      const query = lineItemId ? `?lineItemId=${encodeURIComponent(lineItemId)}` : '';
      return fetchApi<ListAppliedOptimizationsResponse>(
        `/executions/${encodeURIComponent(executionId)}/optimizations${query}`
      );
    },
  },
};
